import { QueryClient, useQuery } from "@tanstack/react-query";
import { portalFetch } from "./api";
import type {
  StudentAnamnesis,
  StudentPortalHistory,
  StudentDietHistory,
  StudentPortalMe,
  StudentPortalPlan,
} from "./types";

/**
 * Cliente React Query — cache em memória das leituras do portal. O legado Next
 * usava `force-dynamic` (refetch a cada navegação); aqui replicamos "dados
 * frescos" com staleTime curto + refetch on focus, sem o custo de SSR.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: 1,
      refetchOnWindowFocus: true,
    },
  },
});

/** ISO de N dias atrás — janela das queries de histórico. */
export function daysAgoISO(days: number): string {
  const ms = Date.now() - days * 24 * 60 * 60 * 1000;
  return new Date(ms).toISOString();
}

export function useMe() {
  return useQuery({
    queryKey: ["me"],
    queryFn: ({ signal }) => portalFetch<StudentPortalMe>("/student-portal/me", { signal }),
  });
}

export function useAnamnesis() {
  return useQuery({
    queryKey: ["anamnesis"],
    queryFn: ({ signal }) =>
      portalFetch<StudentAnamnesis>("/student-portal/me/anamnesis", { signal }),
  });
}

export function usePlan() {
  return useQuery({
    queryKey: ["plan"],
    queryFn: ({ signal }) => portalFetch<StudentPortalPlan>("/student-portal/me/plan", { signal }),
  });
}

export function useWorkoutHistory(days = 90, limit = 50) {
  const from = daysAgoISO(days);
  return useQuery({
    queryKey: ["history", "workout", days, limit],
    queryFn: ({ signal }) =>
      portalFetch<StudentPortalHistory>(
        `/student-portal/me/history?from=${encodeURIComponent(from)}&limit=${limit}`,
        { signal },
      ),
  });
}

export function useDietHistory(days = 90, limit = 100) {
  const from = daysAgoISO(days);
  return useQuery({
    queryKey: ["history", "diet", days, limit],
    queryFn: ({ signal }) =>
      portalFetch<StudentDietHistory>(
        `/student-portal/me/diet-history?from=${encodeURIComponent(from)}&limit=${limit}`,
        { signal },
      ),
  });
}
